import React, { useState } from 'react';
import { ChevronDown, ListFilter, ListX, X } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import { useTagSearch } from '../contexts/TagSearchContext';
import Modal from './Modal';
import { illustrationHome } from '../utils/illustrationRoute';

export type FilterChoice = { value: string; label: string; count?: number };
export function FilterCondition({ label, onRemove, choices = [], value, onSelect }: { label: string; onRemove: () => void; choices?: FilterChoice[]; value?: string; onSelect?: (value: string) => void }) {
    const { t } = useContent();
    const [open, setOpen] = useState(false);
    const selectable = choices.length > 1 && !!onSelect;
    return <><div className="lab-condition lab-chip">
        {selectable ? <button type="button" className="lab-condition-select" title={label} aria-label={label} aria-haspopup="dialog" onClick={() => setOpen(true)}>
            <span className="lab-condition-label">{label}</span><ChevronDown aria-hidden="true" />
        </button> : <span className="lab-condition-label lab-condition-static">{label}</span>}
        <button type="button" className="lab-condition-remove" onClick={onRemove} title={t('search.remove', { label })} aria-label={t('search.remove', { label })}><X aria-hidden="true" /></button>
    </div><Modal isOpen={open} onClose={() => setOpen(false)} title={label}>
        <div className="lab-filter-options">
            {choices.map(choice => <button key={choice.value} type="button" className="lab-button" aria-pressed={choice.value === value} onClick={() => { setOpen(false); onSelect?.(choice.value); }}>
                {choice.label}{choice.count !== undefined && <span> ({choice.count})</span>}
            </button>)}
        </div>
    </Modal></>;
}

export function ClearFiltersButton({ count, onClear }: { count: number; onClear: () => void }) {
    const { t } = useContent();
    return count < 2 ? null : <button type="button" className="lab-clear-filters" aria-label={t('search.clearAll')} title={t('search.clearAll')} onClick={onClear}><ListX aria-hidden="true" /></button>;
}

export function AddFilterButton() {
    const { t } = useContent();
    const { openTagSearch } = useTagSearch();
    return <button type="button" className="lab-add-filter" onClick={openTagSearch} aria-label={t('illustrations.add')} title={t('illustrations.add')}><ListFilter aria-hidden="true" /></button>;
}

export default function AppliedFilters() {
    const { t } = useContent();
    const params = new URLSearchParams(window.location.search);
    const filters: Array<{ key: string; label: string; remove: string[] }> = [];
    if (params.get('year')) filters.push({ key: 'date', label: t('search.month', { year: params.get('year')!, month: params.get('month') || '' }), remove: ['year', 'month'] });
    for (const key of ['word', 'tags', 'color']) {
        if (params.get(key)) filters.push({ key, label: params.get(key)!, remove: [key] });
    }
    const remove = (keys: string[]) => {
        const next = new URLSearchParams(params);
        keys.forEach(key => next.delete(key)); next.delete('pageNumber');
        const empty = !next.has('word') && !next.has('tags') && !next.has('color') && !next.has('year');
        window.history.pushState({}, '', empty ? illustrationHome : '/?' + next); window.dispatchEvent(new PopStateEvent('popstate'));
    };
    return <div className="lab-filter-chips" aria-label={t('search.applied')}>
            {filters.map(filter => <FilterCondition key={filter.key} label={filter.label} onRemove={() => remove(filter.remove)} />)}
            {filters.length === 0 && <span className="lab-label">{t('search.empty')}</span>}
            <ClearFiltersButton count={filters.length} onClear={() => remove(filters.flatMap(filter => filter.remove))} />
            <AddFilterButton />
    </div>;
}
